// QA-010 — central handler for expired/invalid sessions.
//
// When ANY /api/* call returns 401, treat it as the canonical signal that the
// access token (or DB session) is no longer valid. Mirror the legacy app's
// behavior: clear cached user state, call Auth0's hosted logout, and let the
// SPA land on /login with the Sign-In button. Auth0 SPA SDK will then log
// "Auto sign-in blocked: User has logged out" on the next loginWithRedirect
// attempt, exactly matching legacy console output.
//
// WHY THIS EXISTS:
// Pre-fix, api.js + installAuthFetch.js silently swallowed 401s. The SPA
// would re-render the dashboard from localStorage cache (showing stale
// firstName/email but "User ID: Not available" because the fresh /profile
// call failed). Now we react to 401 immediately.
//
// IDEMPOTENT:
// Multiple simultaneous 401s (e.g. profile + db/list firing in parallel)
// all funnel through the `handling` latch so logout only fires once.

import { get } from 'svelte/store';
import { isAuthenticated, logout } from './auth.js';

let handling = false;

export function handleAuthExpired(reason) {
  if (handling) return;

  // Only meaningful if the SPA thought we were signed in. A 401 from a
  // public-page caller (no token in flight, isAuthenticated=false) is not
  // a session-expiry signal — leave it alone.
  if (!get(isAuthenticated)) return;

  handling = true;
  // eslint-disable-next-line no-console
  console.warn(`[auth] Session expired (${reason || '401'}); signing out and returning to home.`);

  // Clear stale local cache so a back-button or fast-reload can't repaint
  // the dashboard from the previous session's user object.
  try {
    localStorage.removeItem('user');
    localStorage.removeItem('roles');
    localStorage.setItem('logout_time', String(Date.now()));
  } catch (_) {
    // localStorage may be unavailable (private mode, quota); proceed with logout regardless.
  }

  // Auth0 hosted logout → returnTo = window.location.origin (the SPA root).
  // Index.svelte routes unauthenticated visitors to /login → Sign-In button.
  logout().catch((e) => {
    // If Auth0 logout itself throws (network, CORS), fall back to a hard
    // navigation to the SPA origin so the user still escapes the broken
    // dashboard state.
    // eslint-disable-next-line no-console
    console.error('[auth] Auth0 logout failed; hard-redirecting to /', e);
    window.location.assign('/');
  });
}
